/**
 * Settings > Audit log (tracker item 32).
 *
 * Read-only list of the tenant's audit rows, newest first. Filters (startDate,
 * endDate, action, actorUserId, targetUserId) and the page live in the query
 * string, like the ledger list. Route-gated to ADMIN (canViewAuditLog),
 * matching the backend's requireRole('ADMIN') on /audit-logs.
 *
 * The action select is filled from GET /audit-logs/actions and the actor
 * select from the users list (deactivated users included). Dates are UTC days
 * on the server, while times are shown in the viewer's time zone.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, IAuditLogsApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, secondaryButtonClass } from '../../components/formStyles';
import { dateParam, dateRangeError, pageParam } from '../reports/reportUi';
import {
  AUDIT_LOG_PAGE_SIZE,
  actorOptionLabel,
  auditActionLabel,
  auditDetailsJson,
  auditLogErrorMessage,
  auditTarget,
  auditTargetTypeLabel,
  auditTime,
} from './auditLogUi';
import { Toast } from '../../components/Toast';

function TargetCell({ row }: { row: types.AuditLogEntry }) {
  const target = auditTarget(row);
  if (!target) return <span className="text-gray-400">—</span>;
  return (
    <>
      <span className="text-gray-500">{auditTargetTypeLabel(target.type)} </span>
      {target.to ? (
        <Link to={target.to} className="text-blue-700 hover:underline">{target.label}</Link>
      ) : (
        <span className="text-gray-900">{target.label}</span>
      )}
    </>
  );
}

export function AuditLogPage() {
  const api = useMemo<IAuditLogsApi & IUsersApi>(() => createAPIClient(), []);
  const [params, setParams] = useSearchParams();
  const page = pageParam(params.get('page'));
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const action = params.get('action') || '';
  const actorUserId = params.get('actorUserId') || '';
  const targetUserId = params.get('targetUserId') || '';
  const rangeError = dateRangeError(startDate, endDate);

  const [rows, setRows] = useState<types.AuditLogEntry[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const [actions, setActions] = useState<string[]>([]);
  const [users, setUsers] = useState<types.UserListItem[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // Filter options. A failure here only degrades the selects; the table still loads.
  useEffect(() => {
    let active = true;
    api.listAuditLogActions()
      .then((result) => { if (active) setActions(result); })
      .catch(() => { if (active) setOptionsError('Could not load every filter option.'); });
    api.listUsers(true)
      .then((result) => { if (active) setUsers([...result].sort((a, b) => a.email.localeCompare(b.email))); })
      .catch(() => { if (active) setOptionsError('Could not load every filter option.'); });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (rangeError) {
      setLoading(false);
      setRows([]);
      setPagination(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    api.listAuditLogs(page, AUDIT_LOG_PAGE_SIZE, { startDate, endDate, action, actorUserId, targetUserId })
      .then((result) => { if (active) { setRows(result.data); setPagination(result.pagination); setExpanded(new Set()); } })
      .catch((cause) => { if (active) setError(auditLogErrorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, page, startDate, endDate, action, actorUserId, targetUserId, rangeError]);

  function changeParams(next: Record<string, string>) {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    setParams(updated);
  }

  function toggle(id: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Keep the selects' current values even when they are not in the loaded options
  // (an action newer than this client, a user deleted since, options still loading).
  const actionOptions = action && !actions.includes(action) ? [...actions, action] : actions;
  const actorMissing = !!actorUserId && !users.some((user) => user.id === actorUserId);
  const targetUser = users.find((user) => user.id === targetUserId);

  const pages = Math.max(1, pagination?.pages || 1);
  const hasFilters = !!(startDate || endDate || action || actorUserId || targetUserId);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Audit Log</h1>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Who changed what: user and role changes, settings, and financial actions such as sending, cancelling or
        voiding documents and posting journal entries. Entries cannot be edited or deleted.
      </p>

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 mb-2">
          <div className="w-full sm:w-auto">
            <label className={labelClass} htmlFor="audit-start">From</label>
            <input id="audit-start" type="date" className={inputClass} value={startDate} onChange={(event) => changeParams({ startDate: event.target.value, page: '' })} />
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelClass} htmlFor="audit-end">To</label>
            <input id="audit-end" type="date" className={inputClass} value={endDate} onChange={(event) => changeParams({ endDate: event.target.value, page: '' })} />
          </div>
          <div className="w-full sm:max-w-xs">
            <label className={labelClass} htmlFor="audit-action">Action</label>
            <select id="audit-action" className={inputClass} value={action} onChange={(event) => changeParams({ action: event.target.value, page: '' })}>
              <option value="">All actions</option>
              {actionOptions.map((value) => <option key={value} value={value}>{auditActionLabel(value)}</option>)}
            </select>
          </div>
          <div className="w-full sm:max-w-xs">
            <label className={labelClass} htmlFor="audit-actor">User</label>
            <select id="audit-actor" className={inputClass} value={actorUserId} onChange={(event) => changeParams({ actorUserId: event.target.value, page: '' })}>
              <option value="">All users</option>
              {users.map((user) => <option key={user.id} value={user.id}>{actorOptionLabel(user)}</option>)}
              {actorMissing && <option value={actorUserId}>{actorUserId}</option>}
            </select>
          </div>
          {hasFilters && (
            <button
              type="button"
              className={`${secondaryButtonClass} w-full sm:w-auto`}
              onClick={() => changeParams({ startDate: '', endDate: '', action: '', actorUserId: '', targetUserId: '', page: '' })}
            >
              Clear filters
            </button>
          )}
        </div>
        <p className="mb-5 text-xs text-gray-500">Dates are UTC days; times are shown in your time zone.</p>

        {targetUserId && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-2 break-words rounded-full bg-blue-50 px-3 py-1 text-blue-800 ring-1 ring-inset ring-blue-600/20">
              Changes to user: <strong className="min-w-0 break-all">{targetUser?.email || targetUserId}</strong>
              <button type="button" aria-label="Remove target user filter" className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 font-bold text-blue-700 hover:text-blue-900" onClick={() => changeParams({ targetUserId: '', page: '' })}>
                ×
              </button>
            </span>
          </div>
        )}

        {optionsError && <Toast kind="warning">{optionsError}</Toast>}
        {rangeError && <Toast kind="error">{rangeError}</Toast>}
        {error && <Toast kind="error">{error}</Toast>}
        {rangeError ? null : loading ? <p>Loading audit log…</p> : error ? null : (
          <>
            {rows.length === 0 ? (
              <p className="py-10 text-center text-gray-600">
                {hasFilters ? 'No audit entries match these filters.' : 'No audit entries yet.'}
              </p>
            ) : (
              <div className="md:overflow-x-auto">
                <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                  <caption className="sr-only">Audit log</caption>
                  <thead className="bg-gray-50">
                    <tr>
                      {['Time', 'User', 'Action', 'Target', 'Details'].map((heading) => (
                        <th key={heading} scope="col" className="px-4 py-3 text-left font-medium text-gray-600">{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row) => {
                      const json = auditDetailsJson(row.details);
                      const isOpen = expanded.has(row.id);
                      const detailsId = `audit-details-${row.id}`;
                      return (
                        <React.Fragment key={row.id}>
                          <tr data-testid="audit-row">
                            <td data-label="Time" data-primary className="px-4 py-3 whitespace-nowrap" title={row.createdAt}>{auditTime(row.createdAt)}</td>
                            <td data-label="User" className="px-4 py-3 break-words">{row.actorEmail}</td>
                            <td data-label="Action" className="px-4 py-3" title={row.action}>{auditActionLabel(row.action)}</td>
                            <td data-label="Target" className="px-4 py-3"><span className="min-w-0"><TargetCell row={row} /></span></td>
                            <td data-label="Details" className="px-4 py-3 whitespace-nowrap">
                              {json === null ? (
                                <span className="text-gray-400">None</span>
                              ) : (
                                <button
                                  type="button"
                                  className="min-h-[44px] sm:min-h-0 text-blue-700 hover:underline"
                                  aria-expanded={isOpen}
                                  aria-controls={detailsId}
                                  onClick={() => toggle(row.id)}
                                >
                                  {isOpen ? 'Hide details' : 'Show details'}
                                </button>
                              )}
                            </td>
                          </tr>
                          {isOpen && json !== null && (
                            <tr id={detailsId} data-testid="audit-details">
                              <td colSpan={5} className="bg-gray-50 px-4 py-3">
                                <pre className="w-full min-w-0 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-800">{json}</pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {pages > 1 && (
              <div className="flex flex-wrap justify-between items-center gap-3 mt-6">
                <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => changeParams({ page: String(page - 1) })}>Previous</button>
                <span className="text-sm text-gray-600">Page {page} of {pages}</span>
                <button type="button" className={secondaryButtonClass} disabled={page >= pages} onClick={() => changeParams({ page: String(page + 1) })}>Next</button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
